import { useCallback, useRef, useState } from "react"

declare global {
	const CropTarget: {
		fromElement: (element: Element) => Promise<any>
	}
}

export type CaptureStatus = "idle" | "requesting" | "recording" | "error"

export interface UseRegionCaptureOptions {
	/** Optional audio constraint to capture tab audio */
	audio?: boolean
	/** Express route for your compiled file. Fires on BOTH native and manual stop events. */
	onComplete: (file: File) => void
}

export function useRegionCapture(options: UseRegionCaptureOptions) {
	const isSupported =
		typeof window !== "undefined" &&
		navigator.mediaDevices &&
		"CropTarget" in window
	const [status, setStatus] = useState<CaptureStatus>("idle")
	const [error, setError] = useState<string | null>(null)

	const mediaRecorderRef = useRef<MediaRecorder | null>(null)
	const streamRef = useRef<MediaStream | null>(null)
	const chunksRef = useRef<Blob[]>([])

	// Keep a stable ref to your callback so startCapture doesn't need to rebuild
	const onCaptureCompleteRef = useRef(options.onComplete)
	onCaptureCompleteRef.current = options.onComplete

	/** internal utility to tear down hardware tracks */
	const cleanupStream = useCallback(() => {
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => {
				track.onended = null
				track.stop()
			})
			streamRef.current = null
		}
	}, [])

	/** internal utility to shut down recorder and trigger file pipeline */
	const handleStopExecution = useCallback(() => {
		if (
			mediaRecorderRef.current &&
			mediaRecorderRef.current.state !== "inactive"
		) {
			mediaRecorderRef.current.stop()
		}
	}, [])

	/** Begins the Region Capture pipeline */
	const startCapture = useCallback(
		async (element: HTMLElement | null) => {
			if (!element) {
				const err = "No valid element provided for region capture."
				setError(err)
				setStatus("error")
				throw new Error(err)
			}

			if (!isSupported) {
				const err =
					"Region Capture API is not fully supported in this environment."
				setError(err)
				setStatus("error")
				throw new Error(err)
			}

			setStatus("requesting")
			setError(null)
			chunksRef.current = []

			try {
				const cropTarget = await CropTarget.fromElement(element)

				const stream = await navigator.mediaDevices.getDisplayMedia({
					video: { displaySurface: "browser" },
					// @ts-ignore
					preferCurrentTab: true,
					audio: options.audio ?? false,
				})

				streamRef.current = stream

				const [videoTrack] = stream.getVideoTracks()
				if (
					"cropTo" in videoTrack &&
					typeof (videoTrack as any).cropTo === "function"
				) {
					await (videoTrack as any).cropTo(cropTarget)
				}

				const codecOptions = MediaRecorder.isTypeSupported(
					"video/webm;codecs=vp9",
				)
					? { mimeType: "video/webm;codecs=vp9" }
					: { mimeType: "video/webm" }

				const recorder = new MediaRecorder(stream, codecOptions)
				mediaRecorderRef.current = recorder

				recorder.ondataavailable = (event) => {
					if (event.data && event.data.size > 0) {
						chunksRef.current.push(event.data)
					}
				}

				// A: Catch browser "Stop Sharing" UI interactions
				videoTrack.onended = () => {
					handleStopExecution()
				}

				// Combined capture resolution engine
				recorder.onstop = () => {
					if (chunksRef.current.length > 0) {
						const blob = new Blob(chunksRef.current, { type: "video/mp4" })
						const file = new File([blob], `region-capture-${Date.now()}.mp4`, {
							type: "video/mp4",
						})

						chunksRef.current = []
						setStatus("idle")
						cleanupStream()

						// Dispatch out to user-land callback
						onCaptureCompleteRef.current(file)
					} else {
						setStatus("idle")
						cleanupStream()
					}
				}

				recorder.start()
				setStatus("recording")
			} catch (err: any) {
				setStatus("error")
				setError(err?.message || String(err))
				cleanupStream()
				throw err
			}
		},
		[cleanupStream, handleStopExecution, isSupported, options.audio],
	)

	/** Manually triggers stop pipeline explicitly via UI action buttons */
	const stopCapture = useCallback(() => {
		handleStopExecution()
	}, [handleStopExecution])

	return {
		startCapture,
		stopCapture,
		status,
		isCapturing: status === "recording",
		error,
		isSupported,
	}
}
